import { useTranslation } from "@sagot/i18n";
import { formatRelative } from "../shared/format";
import type { IdentityProviderOut } from "../shared/types";
import { useAudienceLabel } from "../shared/use-shared-queries";

interface Detail {
  key: string;
  label: string;
  value: string;
  mono?: boolean;
}

function firstCode(row: Record<string, unknown>): number | null {
  const audiences = row.audiences;
  return Array.isArray(audiences) && typeof audiences[0] === "number" ? audiences[0] : null;
}

function useDetails(provider: IdentityProviderOut): Detail[] {
  const { t, i18n } = useTranslation();
  const label = useAudienceLabel();
  const mapsTo = provider.audiences.length > 0 ? provider.audiences.map(label).join(", ") : t("deployKnowledge.audiences.idp.mapsToNone");
  const mappings = (rows: Record<string, unknown>[], key: "group" | "claim") =>
    rows
      .map((row) => {
        const code = firstCode(row);
        const left = key === "claim" ? `${String(row.claim)} = ${String(row.value)}` : String(row.group);
        return code === null ? left : `${left} → ${label(code)}`;
      })
      .join(" · ");
  const details: (Detail | null)[] = [
    provider.issuer ? { key: "issuer", label: t("deployKnowledge.audiences.idp.issuer"), value: provider.issuer, mono: true } : null,
    provider.algorithm ? { key: "algorithm", label: t("deployKnowledge.audiences.idp.algorithm"), value: provider.jwks_url ? `${provider.algorithm} · JWKS` : provider.algorithm, mono: true } : null,
    provider.domain ? { key: "domain", label: t("deployKnowledge.audiences.idp.domain"), value: provider.domain, mono: true } : null,
    provider.client_id ? { key: "client", label: t("deployKnowledge.audiences.idp.clientId"), value: provider.client_id, mono: true } : null,
    provider.claim_mappings.length > 0 ? { key: "claims", label: t("deployKnowledge.audiences.idp.claimMapping"), value: mappings(provider.claim_mappings, "claim"), mono: true } : null,
    provider.group_mappings.length > 0 ? { key: "groups", label: t("deployKnowledge.audiences.idp.groupMapping"), value: mappings(provider.group_mappings, "group"), mono: true } : null,
    { key: "maps", label: t("deployKnowledge.audiences.idp.grants"), value: mapsTo },
    { key: "verified", label: t("deployKnowledge.audiences.idp.lastVerified"), value: provider.last_verified_at ? formatRelative(provider.last_verified_at, i18n.language) : t("deployKnowledge.audiences.idp.never") },
  ];
  return details.filter((d): d is Detail => d !== null);
}

/** Key facts of a configured provider (issuer, algorithm, mappings, last verified token). */
export function ProviderDetails({ provider }: { provider: IdentityProviderOut }) {
  const details = useDetails(provider);
  return (
    <dl className="mt-4 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
      {details.map((d) => (
        <div key={d.key} className="min-w-0 rounded-md bg-surface-2 p-2.5">
          <dt className="text-fg-subtle">{d.label}</dt>
          <dd className={d.mono ? "mt-0.5 break-all font-mono font-medium" : "mt-0.5 font-semibold"}>{d.value}</dd>
        </div>
      ))}
    </dl>
  );
}
